/**
 * Simulador interactivo — POST /api/moderation/simulate.
 *
 * Cada call manda 1 mensaje y la IA devuelve una decisión (respond / escalate /
 * ignore). En el panel guardamos el historial en memoria (no persiste entre
 * recargas — el sandbox por archivo CSV sigue siendo el camino para tests
 * reproducibles).
 */
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { apiPost } from "@/lib/api";
import type { ClientSlug, Platform } from "@/types";

export type SimulateKind = "dm" | "comment";

export interface SimulateTurn {
  role: "user" | "assistant";
  content: string;
}

export interface SimulateInput {
  clientSlug: ClientSlug;
  platform: Platform;
  kind: SimulateKind;
  message: string;
  senderName?: string;
  industry?: string;
  /** Turnos previos del mismo chat — habilita continuidad. */
  history?: SimulateTurn[];
}

export type ModerationDecision =
  | { action: "respond"; response: string }
  | { action: "ignore"; reason: string }
  | { action: "escalate"; category: string; reason: string };

interface SimulateResponse {
  decision: ModerationDecision;
}

export function useSimulateModeration() {
  return useMutation<ModerationDecision, Error, SimulateInput>({
    mutationFn: async (input) => {
      const res = await apiPost<SimulateResponse>("/moderation/simulate", input);
      return res.decision;
    },
    onError: (err) => {
      toast.error(`Simulación falló: ${err.message}`);
    },
  });
}
